/**
 * SafeClaw API Client
 *
 * Centralized HTTP client for all backend API calls.
 * Handles JWT token management, request/response serialization,
 * refresh-token retry, and error normalization.
 */

import { API_BASE } from "./config"

const TOKEN_KEY = "safeclaw_token"
const REFRESH_TOKEN_KEY = "safeclaw_refresh_token"
const USER_KEY = "safeclaw_user"

export interface StoredUser {
  userId: string
  username: string
  nick?: string
  phone?: string
  email?: string
  avatar?: string
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public statusText: string,
    public body?: unknown
  ) {
    super(`API Error ${status}: ${statusText}`)
    this.name = "ApiError"
  }
}

export function getToken(): string | null {
  if (typeof window === "undefined") return null
  return localStorage.getItem(TOKEN_KEY)
}

export function setToken(token: string) {
  localStorage.setItem(TOKEN_KEY, token)
}

export function getRefreshToken(): string | null {
  if (typeof window === "undefined") return null
  return localStorage.getItem(REFRESH_TOKEN_KEY)
}

export function setRefreshToken(token: string) {
  localStorage.setItem(REFRESH_TOKEN_KEY, token)
}

export function setAuthTokens(token: string, refreshToken: string) {
  setToken(token)
  setRefreshToken(refreshToken)
}

export function removeToken() {
  localStorage.removeItem(TOKEN_KEY)
  localStorage.removeItem(REFRESH_TOKEN_KEY)
  localStorage.removeItem(USER_KEY)
}

export function getStoredUser(): StoredUser | null {
  if (typeof window === "undefined") return null
  try {
    const raw = localStorage.getItem(USER_KEY)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

export function setStoredUser(user: StoredUser) {
  localStorage.setItem(USER_KEY, JSON.stringify(user))
}

function buildUrl(path: string): string {
  const cleanPath = path.startsWith("/") ? path : "/" + path

  if (!API_BASE) {
    return cleanPath
  }

  try {
    return new URL(API_BASE + cleanPath).toString()
  } catch {
    throw new ApiError(-1, `Invalid URL: ${API_BASE}${cleanPath}`)
  }
}

function isDiscourseApi(path: string): boolean {
  return path.startsWith("/discourse/") || path.startsWith("/api/v1/")
}

function dispatchAuthExpired() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("auth:expired"))
  }
}

interface RefreshTokenResponse {
  token: string
  refreshToken: string
}

let refreshTokenPromise: Promise<string> | null = null

async function refreshAccessToken(): Promise<string> {
  const token = getToken()
  const refreshToken = getRefreshToken()

  if (!token || !refreshToken) {
    removeToken()
    throw new ApiError(401, "认证已过期，请重新登录")
  }

  const res = await fetch(buildUrl("/discourse/api/v1/user/refresh-token"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${token}`,
    },
    body: JSON.stringify({ refreshToken }),
  })

  const json = await parseJson(res)
  const wrapped = json as { code?: number; msg?: string; data?: unknown }

  if (!res.ok || (wrapped.code !== undefined && wrapped.code !== 200 && wrapped.code !== 0)) {
    removeToken()
    throw new ApiError(
      wrapped.code ?? res.status,
      wrapped.msg || res.statusText || "认证已过期，请重新登录",
      wrapped.data ?? json,
    )
  }

  const data = (wrapped.data !== undefined ? wrapped.data : json) as Partial<RefreshTokenResponse>
  if (!data.token || !data.refreshToken) {
    removeToken()
    throw new ApiError(401, "刷新登录状态失败", data)
  }

  setAuthTokens(data.token, data.refreshToken)
  return data.token
}

async function getOrRefreshAccessToken(): Promise<string> {
  if (!refreshTokenPromise) {
    refreshTokenPromise = refreshAccessToken().finally(() => {
      refreshTokenPromise = null
    })
  }
  return refreshTokenPromise
}

async function parseJson(res: Response): Promise<unknown> {
  const rawText = await res.text()
  try {
    return JSON.parse(rawText)
  } catch {
    throw new ApiError(
      res.status,
      `响应不是合法 JSON (${res.statusText})`,
      { rawBody: rawText.slice(0, 500) },
    )
  }
}

interface FetchOptions {
  method?: string
  body?: unknown
  params?: Record<string, string | number | undefined>
  auth?: boolean
  skipRefresh?: boolean
}

export async function apiFetch<T>(path: string, opts: FetchOptions = {}): Promise<T> {
  const { method = "GET", body, params, auth = true, skipRefresh = false } = opts

  let url = buildUrl(path)
  if (params) {
    const sp = new URLSearchParams()
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== "") sp.set(key, String(value))
    }
    const qs = sp.toString()
    if (qs) url += (url.includes("?") ? "&" : "?") + qs
  }

  const fetchOnce = () => {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    }

    if (auth) {
      const token = getToken()
      if (token) headers["Authorization"] = `Bearer ${token}`
    }

    return fetch(url, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
    })
  }

  let res = await fetchOnce()

  if (res.status === 401 && auth && !skipRefresh && isDiscourseApi(path)) {
    try {
      await getOrRefreshAccessToken()
      res = await fetchOnce()
    } catch {
      dispatchAuthExpired()
      throw new ApiError(401, "认证已过期，请重新登录")
    }
  }

  const json = await parseJson(res)
  const wrapped = json as { code?: number; msg?: string; data?: unknown }

  if (wrapped.code !== undefined && wrapped.code !== 200 && wrapped.code !== 0) {
    if (wrapped.code === 401 && auth && !skipRefresh && isDiscourseApi(path)) {
      try {
        await getOrRefreshAccessToken()
        return apiFetch<T>(path, { ...opts, skipRefresh: true })
      } catch {
        dispatchAuthExpired()
        throw new ApiError(401, "认证已过期，请重新登录")
      }
    }

    if (wrapped.code === 401 && isDiscourseApi(path)) {
      dispatchAuthExpired()
    }

    throw new ApiError(
      wrapped.code,
      wrapped.msg || res.statusText,
      wrapped.data
    )
  }

  if (!res.ok && wrapped.code === undefined) {
    if (res.status === 401 && isDiscourseApi(path)) {
      dispatchAuthExpired()
    }
    throw new ApiError(res.status, res.statusText, json)
  }

  return (wrapped.data !== undefined ? wrapped.data : json) as T
}
